import {
  Badge,
  Box,
  Button,
  FormControl,
  HStack,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Plus } from 'lucide-react';
import { AsyncSelect, MultiValue, SingleValue } from 'chakra-react-select';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { ReactNode, useState, useCallback } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useDebounceCallback } from 'usehooks-ts';

type Option = {
  label: ReactNode;
  value: string;
};

export function AddUnitsModal({
  courseId,
  currentCourseItems,
}: {
  courseId: string;
  currentCourseItems: NonNullable<RouterOutputs['courses']['byId']>['courseItems'];
}) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selected, setSelected] = useState<string | null>(null);
  const toast = useToast();
  const trpcUtils = trpcAdmin.useUtils();

  const updateCourseItems = trpcAdmin.courses.updateCourseItems.useMutation({
    onSuccess: () => {
      toast({
        title: t`Unit added successfully`,
        status: 'success',
        isClosable: true,
        duration: 3000,
      });

      void trpcUtils.courses.byId.invalidate(courseId);
    },
    onError: (error) => {
      toast({
        title: t`Failed to add unit`,
        description: error.message,
        status: 'error',
        isClosable: true,
        duration: 5000,
      });
    },
  });

  const handleAddUnit = useCallback(async () => {
    if (selected == null) {
      return;
    }
    const currentUnits = currentCourseItems
      .map((item) => item.unit_of_competency_id)
      .filter((unit) => unit !== null);

    if (currentUnits?.includes(selected)) {
      toast({
        title: t`This unit is already added to the course`,
        status: 'error',
        isClosable: true,
      });
    } else {
      currentUnits.push(selected);

      if (currentUnits == null) {
        return;
      }
      await updateCourseItems.mutateAsync({
        courseId,
        unitsIdList: currentUnits,
      });
      onClose();
    }
  }, [courseId, toast, currentCourseItems, onClose, selected, updateCourseItems]);

  const debouncedLoadOptions = useDebounceCallback(
    async (inputValue: string, callback: (options: Option[]) => void) => {
      const data = await trpcUtils.unitsOfCompetency.find.fetch(inputValue);

      const options = data.map(
        (unit) =>
          ({
            value: unit.id,
            label: (
              <CustomUnitsOfCompetencySelectorOption
                title={unit.title}
                code={unit.code}
                contextCategory={unit.unitContextCategory?.categoryName ?? null}
              />
            ),
          }) satisfies Option,
      );
      callback(options);

      return Promise.resolve(options);
    },
    300,
  );

  const handleChangeValue = useCallback(
    (selectedOption: MultiValue<Option> | SingleValue<Option>) => {
      if (
        selectedOption != null &&
        'value' in selectedOption &&
        selectedOption?.label != null &&
        selectedOption?.value != null
      ) {
        setSelected(selectedOption?.value);
      } else {
        setSelected(null);
      }
    },
    [setSelected],
  );

  return (
    <>
      <IconButton
        aria-label={t`Add unit of competency`}
        icon={<Icon as={Plus} color="fg.default" boxSize="24px" />}
        variant="ghost"
        size="sm"
        onClick={onOpen}
      />

      <Modal onClose={onClose} isOpen={isOpen} isCentered size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Trans>Add unit to course</Trans>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <AsyncSelect
                name="unitId"
                placeholder={t`Search for a unit`}
                defaultOptions
                loadOptions={debouncedLoadOptions}
                onChange={handleChangeValue}
                selectedOptionColorScheme="brand"
                isClearable={true}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose} mr={'auto'} variant="ghost">
              <Trans>Close</Trans>
            </Button>
            <Button isDisabled={selected == null} onClick={handleAddUnit}>
              <Trans>Add</Trans>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

function CustomUnitsOfCompetencySelectorOption({
  title,
  code,
  contextCategory,
}: {
  title: string;
  code: string;
  contextCategory: string | null;
}) {
  return (
    <Box>
      <HStack spacing={2}>
        <Badge colorScheme="blue" fontSize="xs" flexShrink={0} fontFamily="mono">
          {code}
        </Badge>
        <Text fontSize="sm" noOfLines={1}>
          {title}
        </Text>
      </HStack>
      {contextCategory != null ? (
        <Badge fontSize="2xs" colorScheme="gray" fontWeight="normal" mt={1}>
          {contextCategory}
        </Badge>
      ) : null}
    </Box>
  );
}
